/**
 * PostHog Wrapper with Comprehensive Deduplication
 * 
 * This module provides a wrapper around PostHog that automatically deduplicates
 * ALL PostHog operations, including automatic tracking events.
 * 
 * Usage:
 *   import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
 *   const posthog = useDeduplicatedPostHog();
 *   posthog.capture('event', { ... });
 *   posthog.setPersonProperties({ ... });
 *   posthog.identify(userId, properties);
 */

import { usePostHog } from 'posthog-js/react';
import { deduplicatedPostHogCall } from './posthogDeduplication';

/**
 * React hook that returns a deduplicated PostHog instance
 * All PUBLIC method calls are automatically deduplicated
 * Internal methods are not wrapped to preserve PostHog functionality
 */
export function useDeduplicatedPostHog() {
  const posthog = usePostHog();
  
  if (!posthog) {
    return null;
  }
  
  // List of PUBLIC methods we want to deduplicate
  // Don't wrap internal/private methods (those starting with _ or internal methods)
  const PUBLIC_METHODS_TO_DEDUPE = [
    'capture',
    'identify',
    'setPersonProperties',
    'setPersonPropertiesForFlags',
    'alias',
    'reset',
    'register',
    'registerOnce',
    'unregister',
    'group',
  ];
  
  // Return a proxy that only intercepts specific public methods
  return new Proxy(posthog, {
    get(target, prop: string | symbol) {
      const original = target[prop as keyof typeof target];
      
      // Only wrap PUBLIC methods we want to deduplicate
      if (typeof original === 'function' && typeof prop === 'string' && PUBLIC_METHODS_TO_DEDUPE.includes(prop)) {
        return (...args: any[]) => {
          return deduplicatedPostHogCall(target, prop, ...args);
        };
      }
      
      // For all other properties/methods, return as-is (no wrapping)
      return original;
    },
  });
}

/**
 * Direct wrapper function for PostHog instance (non-hook version)
 * Use this when you have a PostHog instance but can't use the hook
 * 
 * @param posthog - PostHog instance to wrap
 * @returns Wrapped PostHog instance with deduplication
 */
export function wrapPostHogWithDeduplication(posthog: any) {
  if (!posthog) {
    return null;
  }
  
  // List of PUBLIC methods we want to deduplicate
  const PUBLIC_METHODS_TO_DEDUPE = [
    'capture',
    'identify',
    'setPersonProperties',
    'setPersonPropertiesForFlags',
    'alias',
    'reset',
    'register',
    'registerOnce',
    'unregister',
    'group',
  ];
  
  return new Proxy(posthog, {
    get(target, prop: string | symbol) {
      const original = target[prop as keyof typeof target];
      
      // Only wrap PUBLIC methods we want to deduplicate
      if (typeof original === 'function' && typeof prop === 'string' && PUBLIC_METHODS_TO_DEDUPE.includes(prop)) {
        return (...args: any[]) => {
          return deduplicatedPostHogCall(target, prop, ...args);
        };
      }
      
      // For all other properties/methods, return as-is (no wrapping)
      return original;
    },
  });
}

